import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getConsent, saveConsent, applyConsent } from "../lib/consent";

const ConsentContext = createContext(null);

export const useConsent = () => useContext(ConsentContext);

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (c) {
      setConsent(c);
      applyConsent(c);
    } else {
      setBannerOpen(true);
    }
  }, []);

  const persist = useCallback((prefs) => {
    const c = saveConsent(prefs);
    setConsent(c);
    applyConsent(c);
    setBannerOpen(false);
    setPrefsOpen(false);
  }, []);

  const acceptAll = useCallback(
    () => persist({ analytics: true, external: true }),
    [persist],
  );
  const rejectAll = useCallback(
    () => persist({ analytics: false, external: false }),
    [persist],
  );
  const savePreferences = useCallback((prefs) => persist(prefs), [persist]);
  const openPreferences = useCallback(() => setPrefsOpen(true), []);
  const grantExternal = useCallback(() => {
    const base = getConsent() || { analytics: false, external: false };
    persist({ analytics: base.analytics, external: true });
  }, [persist]);

  const value = useMemo(
    () => ({
      consent,
      bannerOpen,
      prefsOpen,
      setPrefsOpen,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
      grantExternal,
    }),
    [
      consent,
      bannerOpen,
      prefsOpen,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
      grantExternal,
    ],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}
